import { Trash } from '@phosphor-icons/react';
import { cloneElement, ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useExtractorVendorMappingActions } from 'companySettings/hooks';
import { IExtractorVendorMapping } from 'companySettings/types';
import { Button, Confirmation } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { useAgGridClientTableContext } from 'core/states';
import { Color, Variant } from 'theme/enums';

interface IProps {
  extractorVendorMappings?: IExtractorVendorMapping[];
  buttonElement?: ReactElement;
  notificationPosition?: NotificationPosition;
  onRemoveSuccess?: () => void;
}

export const RemoveExtractorVendorMappingsButton = ({
  extractorVendorMappings,
  buttonElement,
  notificationPosition = NotificationPosition.TOP,
  onRemoveSuccess,
}: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const { t } = useTranslation();

  const { deselectAllRows, selectedRows } = useAgGridClientTableContext();

  const mappingsToRemove = useMemo(
    () => (extractorVendorMappings ?? selectedRows ?? []) as IExtractorVendorMapping[],
    [extractorVendorMappings, selectedRows]
  );

  const { removeExtractorVendorMappings } = useExtractorVendorMappingActions({
    position: notificationPosition,
    selectionCount: mappingsToRemove.length,
    // A caller that takes over the aftermath also owns the refresh: the Edit drawer has to close
    // before the refetch destroys the row it is rendered by, or its notification dies with it.
    hasDeferredListRefresh: Boolean(onRemoveSuccess),
  });

  const onRemove = useCallback(async () => {
    setIsConfirmationOpen(false);

    // An empty selection would still be posted: `getFormData` falls past both its array branch
    // (guarded on `length > 0`) and its object branch, and appends the stringified `[]` — so the BE
    // receives `ids=`, an empty string the endpoint defines no behaviour for. Never send it.
    if (!mappingsToRemove.length) {
      return;
    }

    try {
      const { success } = await removeExtractorVendorMappings(mappingsToRemove.map(({ id }) => id));

      if (!success) {
        return;
      }
    } catch {
      // useHandleErrors already surfaced the server message.
      return;
    }

    // The removed rows leave the grid when the invalidated list query comes back; the selection has
    // to be dropped here, while the rows it points at still exist.
    if (!extractorVendorMappings) {
      deselectAllRows();
    }

    onRemoveSuccess?.();
  }, [
    deselectAllRows,
    extractorVendorMappings,
    mappingsToRemove,
    onRemoveSuccess,
    removeExtractorVendorMappings,
  ]);

  const button = useMemo(() => {
    if (buttonElement) {
      return cloneElement(buttonElement, { onClick: () => setIsConfirmationOpen(true) });
    }

    return (
      <Button
        text={t('buttonLabelRemove')}
        color={Color.DANGER}
        variant={Variant.SOFT}
        startDecorator={<Trash />}
        onClick={() => setIsConfirmationOpen(true)}
        disabled={!mappingsToRemove.length}
      />
    );
  }, [buttonElement, mappingsToRemove.length, t]);

  return (
    <>
      {button}

      <Confirmation
        isOpen={isConfirmationOpen}
        title={t('titleRemoveExtractorVendorMapping', { count: mappingsToRemove.length })}
        message={t('messageExtractorVendorMappingRemoveConfirmation', {
          count: mappingsToRemove.length,
        })}
        confirmBtnLabel={t('buttonLabelRemove')}
        confirmBtnColor={Color.DANGER}
        onConfirm={onRemove}
        onCancel={() => setIsConfirmationOpen(false)}
      />
    </>
  );
};
